import VTCMenu from "../../web/components/VTCMenu";
import Breadcrumbs from "../../web/components/Breadcrumbs";
import { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Pagination,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import LoadingOverlay from "react-loading-overlay";
import EnterpriseService from "../../common/services/enterprise-service";
import { EnterpriseContext } from "./EnterpriseContext";
import truckyService from "../../common/services/trucky-service";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import Paginator from "../ui/Paginator";
import { Link } from "react-router-dom";
import DatePicker from "react-date-picker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getZonedTimeFormatted } from "../../web/utils";

const reaons = [
  "maintenance_started",
  "garage_deleted",
  "garage_upgraded",
  "garage_created",
  "vehicle_assigned",
  "vehicle_created",
  "vehicle_deleted",
  "loan_requested",
  "loan_repayment",
  "rank_created",
  "rank_deleted",
  "role_created",
  "role_deleted",
  "user_unbanned",
  "user_banned",
  "got_kicked",
  "left",
  "joined",
  "kick",
  "economy_reset",
  "old_owner",
  "new_owner",
  "role_assigned",
  "application_rejected",
  "application_accepted",
  "company_reset",
];

export default function CompanyLogs({ container }) {
  const { globalContext } = useContext(EnterpriseContext);
  const [loadingData, setLoadingData] = useState(true);
  const [logs, setLogs] = useState([]);
  const es = new EnterpriseService();
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 50,
    total: 0,
    totalPages: 0,
  });

  const [searchParameters, setSearchParameters] = useState({
    search: null, // Placeholder for search functionality
    reason: null, // Placeholder for reason filter
    date_from: null, // Placeholder for date range start
    date_to: null, // Placeholder for date range end
  });

  useEffect(() => {
    truckyService.setDocumentTitle("Company Logs");
  }, []);

  if (!es.can(globalContext.member, "company.*")) {
    return;
  }

  useEffect(() => {
    getLogs();
  }, [pagination.page, pagination.perPage]);

  const getLogs = async () => {
    setLoadingData(true);
    try {
      const response = await es.getCompanyLogs(globalContext.company.id, {
        page: pagination.page,
        perPage: pagination.perPage,
        ...searchParameters,
      });
      if (response && response.data) {
        setLogs(response.data);
        setPagination({
          ...pagination,
          total: response.total,
          totalPages: response.last_page,
        });
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleRowsPerPageChange = (perPage) => {
    setPagination({ ...pagination, page: 1, perPage: perPage });
  };

  const handlePageClick = (event) => {
    setPagination({ ...pagination, page: event.selected + 1 });
  };

  return (
    <LoadingOverlay
      active={loadingData}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row noGutters className="" id="enterpriseContainer">
        <VTCMenu active={"/applications"} container={container}></VTCMenu>
        <Col>
          <Row noGutters>
            <Col>
              <Card className="h-100">
                <CardBody>
                  <Breadcrumbs
                    breadcrumbs={[
                      {
                        to: "/vtchub",
                        title: container.localeManager.strings.vtcHub,
                      },
                      {
                        title: container.localeManager.strings.companySettings,
                      },
                      {
                        title: container.localeManager.strings.logs,
                      },
                    ]}
                  ></Breadcrumbs>
                  <div className="page-hero mb-4">
                    <span className="page-hero-icon">
                      <FontAwesomeIcon icon="clipboard-list" className="mx-0" />
                    </span>
                    <div className="flex-1">
                      <h1 className="page-hero-title mb-0">{container.localeManager.strings.logs}</h1>
                    </div>
                  </div>
                  <div className="filters-toolbar mb-3">
                  <FormGroup row>
                    <Col sm={3}>
                      <Label>{container.localeManager.strings.search}</Label>
                      <Input
                        onChange={(e) =>
                          setSearchParameters({
                            ...searchParameters,
                            search: e.target.value,
                          })
                        }
                      ></Input>
                    </Col>
                    <Col sm={3}>
                      <Label>{container.localeManager.strings.reason}</Label>
                      <Input
                        type="select"
                        value={searchParameters.reason}
                        onChange={(e) =>
                          setSearchParameters({
                            ...searchParameters,
                            reason: e.target.value,
                          })
                        }
                      >
                        <option value="">
                          {container.localeManager.strings.all}
                        </option>
                        {reaons.sort().map((reason, index) => (
                          <option key={index} value={reason}>
                            {container.localeManager.strings[
                              `company_log_${reason}`
                            ] || reason}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={3}>
                      <Label>{container.localeManager.strings.dateFrom}</Label>
                      <DatePicker
                        locale={container.localeManager.language}
                        calendarIcon={
                          <FontAwesomeIcon icon="calendar-alt"></FontAwesomeIcon>
                        }
                        clearIcon={null}
                        onChange={(e) =>
                          setSearchParameters({
                            ...searchParameters,
                            date_from: e,
                          })
                        }
                        value={searchParameters.date_from}
                        showWeekNumbers
                        className="w-100"
                      />
                    </Col>
                    <Col sm={3}>
                      <Label>{container.localeManager.strings.dateTo}</Label>
                      <DatePicker
                        locale={container.localeManager.language}
                        calendarIcon={
                          <FontAwesomeIcon icon="calendar-alt"></FontAwesomeIcon>
                        }
                        clearIcon={null}
                        onChange={(e) =>
                          setSearchParameters({
                            ...searchParameters,
                            date_to: e,
                          })
                        }
                        value={searchParameters.date_to}
                        showWeekNumbers
                        className="w-100"
                      />
                    </Col>
                  </FormGroup>
                  <div className="d-flex justify-content-end mb-3">
                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => {
                        getLogs();
                      }}
                    >
                      {container.localeManager.strings.search}
                    </Button>
                  </div>
                  </div>
                  <Paginator
                    rowsPerPageSelected={pagination.perPage}
                    handleRowsPerPageChange={handleRowsPerPageChange}
                    showRowsPerPage={true}
                    forcePage={pagination.page - 1}
                    totalPages={pagination.totalPages}
                    handlePageClick={handlePageClick}
                  ></Paginator>
                  {logs && logs.length && logs.length > 0 ? (
                    <Table
                      responsive
                      striped
                      size="sm"
                      className="table-row-dashed table-row-gray-300 align-middle gs-0 mt-3"
                      hover
                    >
                      <thead>
                        <tr>
                          <th>{container.localeManager.strings.date}</th>
                          <th>{container.localeManager.strings.user}</th>
                          <th>{container.localeManager.strings.reason}</th>
                          <th>{container.localeManager.strings.details}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log, index) => (
                          <tr key={index}>
                            <td>
                              {getZonedTimeFormatted(log.created_at, "Pp")}
                            </td>
                            <td>
                              <div class="d-flex align-items-center">
                                <div>
                                  <img
                                    className="avatar h-30px min-w-30px rounded me-3"
                                    src={log.user?.avatar_url}
                                  />
                                </div>
                                <div>
                                  <Link to={`/user/${log.user_id}`}>
                                    {log.user?.name}
                                  </Link>
                                </div>
                              </div>
                            </td>
                            <td>
                              {container.localeManager.strings[
                                `company_log_${log.reason}`
                              ] || log.reason}
                            </td>
                            <td>{log.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p className="text-center">
                      {container.localeManager.strings.noLogsAvailable}
                    </p>
                  )}
                  <Paginator
                    rowsPerPageSelected={pagination.perPage}
                    handleRowsPerPageChange={handleRowsPerPageChange}
                    showRowsPerPage={true}
                    forcePage={pagination.page - 1}
                    totalPages={pagination.totalPages}
                    handlePageClick={handlePageClick}
                  ></Paginator>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Col>
        <aside className="ad-column-placeholder">
          <BoxDynamicAd container={container} className="my-3" />
        </aside>
      </Row>
    </LoadingOverlay>
  );
}
